<template>
	<main>
		<LoadingIcon v-if="isLoading" alignment="center" />
		<div v-if="!isLoading && alert && alert.hasOwnProperty('details')">
			<div class="alert-container">
				<div class="alert-type-container">
					<AlertTypeIcon :alertType="alert.type" />
					<div class="alert-type">
						<span class="type">{{ alert.type }}</span>
						<span>{{
							alert.type === 'Birthday'
								? getDateString(alert.alertDateTime)
								: alert.value
						}}</span>
					</div>
				</div>
				<div class="button-container">
					<BaseButton
						color="blue"
						@onClick="handleSendEmail"
						:disabled="
							buttonText !== 'Send Email' ||
							mainProfileIsInternalUser
						"
					>
						{{ buttonText }}
					</BaseButton>
					<QuickActionsMenu
						:lightButton="true"
						:lightButtonIcon="['far', 'ellipsis-h']"
						:menus="populateQuickActions()"
						:status="alert.status"
						@sendToBlink="$emit('sendToBlinkClick')"
						@viewInEase="openInEase(alert.loan.easeLoanUrl)"
						@markAsToDo="
							handleUpdateAlertStatus(
								STATUS_ACTION_TYPES.TO_DO,
								alert.alertId,
							)
						"
						@markAsDone="
							handleUpdateAlertStatus(
								STATUS_ACTION_TYPES.DONE,
								alert.alertId,
							)
						"
						@markAsDismissed="
							handleUpdateAlertStatus(
								STATUS_ACTION_TYPES.DISMISSED,
								alert.alertId,
							)
						"
					/>
				</div>
			</div>
			<div class="alert-details-container">
				<p class="alert-description">{{ alert.alertDesc }}</p>
				<div class="alert-details">
					<div
						v-for="(detail, index) in alert.details"
						class="detail"
						:key="index"
					>
						<span class="name">{{ detail.displayName }}</span>
						<span
							v-if="detail.displayType === DISPLAY_TYPES.STATUS"
						>
							<AlertStatus :status="detail.value" />
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.LEAD_LINK
							"
						>
							<TextButton
								:title="
									truncateModalFields({
										fieldName: detail.value,
										fieldLength: 22,
										attributeName: 'title',
									})
								"
								:ariaLabel="`for ${detail.value}`"
								@onClick="$emit('selectName')"
							>
								{{
									truncateModalFields({
										fieldName: detail.value,
										fieldLength: 22,
									})
								}}
							</TextButton>
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.EMAIL
							"
							:title="
								truncateModalFields({
									fieldName: detail.value,
									fieldLength: 22,
									attributeName: 'title',
								})
							"
						>
							<a
								class="link b360-handle-overflow"
								:class="{ disabled: mainProfileIsInternalUser }"
								role="button"
								:aria-label="`Send an e-mail to ${detail.value}`"
								:href="`mailto:${detail.value}`"
							>
								{{
									truncateModalFields({
										fieldName: detail.value,
										fieldLength: 22,
									})
								}}
							</a>
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.PHONE
							"
						>
							<a
								class="link"
								:class="{ disabled: mainProfileIsInternalUser }"
								role="button"
								:aria-label="`Call ${detail.value}`"
								:href="`tel:${detail.value}`"
							>
								{{ formattedPhoneNumber(detail.value) }}
							</a>
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.LOAN_LINK
							"
						>
							<span
								@click="handleLoanClick(detail.id)"
								class="link"
								role="button"
								:aria-label="`Loan details for address ${detail.value}`"
							>
								{{ detail.value }}
							</span>
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.DATE
							"
							class="value"
						>
							{{
								new Date(detail.value).toLocaleDateString(
									'en-US',
									{
										year: 'numeric',
										month: 'long',
										day: 'numeric',
									},
								)
							}}
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.PERCENTAGE
							"
							class="value"
						>
							{{
								getPercentageValue(
									roundToHundredth(detail.value),
								)
							}}
						</span>
						<span
							v-else-if="
								detail.displayType === DISPLAY_TYPES.CURRENCY
							"
							class="value"
						>
							{{ getFormattedCurrency(detail.value) }}
						</span>
						<span v-else class="value">
							{{ detail.value }}
						</span>
					</div>
				</div>
			</div>
			<section class="alert-notes-container">
				<div class="alert-notes-header">
					<span class="alert-notes-title">Notes</span>
					<TextButton
						@onClick="$emit('addNote')"
						:disabled="mainProfileIsInternalUser"
					>
						Add Note
					</TextButton>
				</div>
				<ul v-if="!isEmpty(alert.notes)" class="alert-notes-list">
					<li
						v-for="(note, index) in alert.notes"
						:key="index"
						class="alert-note"
					>
						<div class="alert-note-info">
							<div class="alert-note-details">
								<span class="alert-note-name">{{
									`${note.firstName} ${note.lastName}`
								}}</span>
								<span class="alert-note-date">{{
									new Date(
										note.createdDateTime,
									).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'long',
										day: 'numeric',
									})
								}}</span>
							</div>
						</div>
						<div class="alert-note-text">
							{{ note.content }}
						</div>
					</li>
				</ul>
				<div v-else class="alert-note no-notes-message">
					<font-awesome-icon
						class="no-notes-icon"
						:icon="['fal', 'sticky-note']"
					/>
					<div class="no-notes-text">There are no notes</div>
				</div>
			</section>
		</div>
	</main>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { isEmpty } from 'lodash';

import AlertStatus from '../AlertStatus.vue';
import AlertTypeIcon from '../AlertTypeIcon.vue';
import BaseButton from '@/components/global/BaseButton.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import TextButton from '@/components/global/TextButton.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import {
	formattedPhoneNumber,
	getFormattedCurrency,
	getPercentageValue,
	roundToHundredth,
	openInEase,
	truncateModalFields,
} from '../helpers';
import { VIEWS, DISPLAY_TYPES } from './constants';
import { STATUS_ACTION_TYPES } from '../constants';
import { CAMPAIGN_ID } from '../../../constants/brand-builder';
import alertsService from '@/services/contacts/alerts-service.js';

export default {
	name: 'AlertDetails',
	components: {
		AlertStatus,
		AlertTypeIcon,
		BaseButton,
		LoadingIcon,
		TextButton,
		QuickActionsMenu,
	},
	props: {
		contactId: Number,
		alertId: Number,
		blinkDisabled: Boolean,
		loggedInUserId: Number,
	},
	computed: {
		...mapState({
			alerts: (state) => state.clientConnectV2.alerts.items,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		buttonText() {
			let text;
			switch (true) {
				case this.alert.status === 'Scheduled':
					text = 'Email Scheduled';
					break;
				case this.alert.emailSentInd:
					text = 'Email Sent';
					break;
				default:
					text = 'Send Email';
			}
			return text;
		},
	},
	data() {
		return {
			isLoading: false,
			alert: null,
			DISPLAY_TYPES,
			VIEWS,
			STATUS_ACTION_TYPES,
		};
	},
	methods: {
		...mapActions(['getAlertDetailsById', 'updateAlertStatus']),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		formattedPhoneNumber,
		getFormattedCurrency,
		getPercentageValue,
		roundToHundredth,
		openInEase,
		truncateModalFields,
		handleLoanClick(loanId) {
			this.$emit('onLoanClick', loanId);
		},
		handleSendEmail() {
			const campaignId = CAMPAIGN_ID[this.alert.type];
			this.$router.push({
				path: `/campaign-editor-workspace/${campaignId}/media`,
				query: {
					alertId: this.alertId,
					borrowerFirstName: this.alert.lead.firstName,
					borrowerLastName: this.alert.lead.lastName,
					email: this.alert.lead.email,
					leadId: this.alert.lead.leadId,
					alertType: this.alert.type,
				},
			});
		},
		async getAlertDetails() {
			if (this.alertId) {
				this.alert = this.alerts.find(
					(alert) => alert.alertId === this.alertId,
				);
				if (!this.alert) {
					await this.getAlertById();
				} else if (
					this.alert &&
					!Object.prototype.hasOwnProperty.call(this.alert, 'details')
				) {
					await this.getAlertById();
				}
			}
		},
		async getAlertById() {
			this.isLoading = true;
			this.alert = await this.getAlertDetailsById({
				alertId: this.alertId,
			});
			// TODO: fix this ESLint error
			// eslint-disable-next-line no-return-assign
			setTimeout(() => (this.isLoading = false), 100);
		},
		async handleUpdateAlertStatus(statusId, alertId) {
			const payload = {
				alertIds: [alertId],
				statusId,
			};
			try {
				await alertsService.updateAlertStatus(payload);
				this.$emit('statusUpdated');
				await this.getAlertDetails();
			} catch (error) {
				this.error = error;
			}
		},
		async getNotes() {
			try {
				const alertNotes = await alertsService.getAlertNotes(
					this.alertId,
				);
				this.alert.notes = alertNotes;
			} catch (error) {
				this.error = error;
			}
		},
		getDateString(date) {
			return new Date(date).toLocaleDateString('en-US', {
				month: 'long',
				day: 'numeric',
			});
		},
		populateQuickActions() {
			const alertStatusUnchangeable =
				this.alert.statusId === STATUS_ACTION_TYPES.DONE_FOR_YOU ||
				this.alert.statusId === STATUS_ACTION_TYPES.SCHEDULED;
			const menu = [
				[
					{
						icon: ['far', 'file-import'],
						text: 'Send to Blink+',
						action: 'sendToBlink',
						disabled:
							this.blinkDisabled ||
							this.mainProfileIsInternalUser ||
							this.loggedInUserId !== this.contactId,
					},
				],
				[
					{
						icon: ['far', 'circle'],
						text: 'Mark as To-Do',
						status: 'To-Do',
						action: 'markAsToDo',
						disabled:
							this.mainProfileIsInternalUser ||
							alertStatusUnchangeable,
					},
					{
						icon: ['far', 'check-circle'],
						text: 'Mark as Done',
						status: 'Done',
						action: 'markAsDone',
						disabled:
							this.mainProfileIsInternalUser ||
							alertStatusUnchangeable,
					},
					{
						icon: ['far', 'times-circle'],
						text: 'Mark as Dismissed',
						status: 'Dismissed',
						action: 'markAsDismissed',
						disabled:
							this.mainProfileIsInternalUser ||
							(alertStatusUnchangeable &&
								this.alert.type !== 'Review Me'),
					},
				],
			];

			if (this.alert.loan) {
				menu[0].push({
					icon: ['far', 'external-link-alt'],
					text: 'View in EASE',
					action: 'viewInEase',
					disabled:
						!this.alert.lead.uwmInd || !this.alert.loan.easeLoanUrl,
				});
			}

			return menu;
		},
	},
	async created() {
		await this.getAlertDetails();
		await this.getNotes();
	},
};
</script>

<style lang="scss" scoped>
.alert-container,
.alert-details-container {
	@include rem('padding', 18px 20px);
	border-bottom: 1px solid $border-color-light;
}

.alert-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-type-container,
.button-container {
	display: flex;
}

.button-container {
	.base-button {
		width: fit-content;
		padding: 0 10px;
	}
}

.alert-type {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	@include rem('margin-left', 15px);

	.type {
		color: $brand-blue;
		font-weight: 500;
	}
}

.quick-actions-container {
	@include rem('margin-left', 15px);

	/deep/ .btn-quick-actions {
		@include rem('width', 44px);
		@include rem('height', 44px);
	}
}

.alert-details-container {
	.alert-details {
		display: grid;
		grid-template-columns: 50% auto;
	}

	.detail {
		display: flex;
		flex-direction: column;
		@include rem('margin-bottom', 22px);

		.name {
			font-weight: 500;
			@include rem('margin-bottom', 8px);
		}
	}

	.alert-description {
		line-height: 1.5;
	}
}

.link {
	color: $brand-blue;
	font-weight: 500;
	text-decoration: none;
	cursor: pointer;
	&.disabled {
		font-weight: 400;
		color: $black;
		pointer-events: none;
	}
}

.spinner {
	@include rem('padding', 25px);
	display: flex;
	justify-content: center;
	align-items: center;
}

.alert-notes {
	&-header {
		@include rem('margin-bottom', 12px);
		display: flex;
		justify-content: space-between;
	}

	&-title {
		color: $black;
		@include typo--roboto__medium;
		@include rem('line-height', 21px);
	}

	&-container {
		@include rem('padding', 25px 20px 20px);
	}

	&-list {
		list-style-type: none;
		margin: 0;
		padding-left: 0;

		& > * + * {
			@include rem('margin-top', 15px);
		}
	}
}

.alert-note {
	border: 1px solid $border-color-light;
	border-radius: 6px;
	background-color: $white;
	@include rem('padding', 20px);

	&.no-notes-message {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		@include rem('padding', 30px);
	}

	&-details {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		& > * {
			@include rem('line-height', 21px);
		}
	}

	&-user-icon {
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 8px;
		@include rem('width', 44px);
		@include rem('height', 44px);
		@include rem('font-size', 20px);
		@include rem('margin-right', 15px);
		background: $brand-blue;
		color: $white;
	}

	&-name {
		color: $black;
		@include typo--roboto__medium;
	}

	&-text {
		@include rem('margin-top', 15px);
		@include rem('line-height', 25px);
	}
}

.no-notes-icon {
	color: $brand-blue;
	@include rem('font-size', 30px);
}

.no-notes-text {
	@include rem('margin-top', 10px);
}
</style>
